import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import {
  ForecastChangeSignalsTable,
  type ForecastChangeSignalRow,
} from '@/features/forecasts/components/home/ForecastChangeSignalsTable';
import { BFG_DEFAULT_LEADTIME_SPAN, DEFAULT_FCTS } from '@/features/forecasts/config/fctRuns';
import {
  HOME_DFU_PAIRS,
  pairKey,
  useHomeForecastChanges,
  type HomeForecastChange,
} from '@/features/forecasts/hooks/useHomeForecastChange';
import { availableTargetWeeks } from '@/features/forecasts/utils/forecastWindow';

const EMPTY_CHANGE: HomeForecastChange = {
  delta: null,
  deltaPercent: null,
  currentMean: null,
  lastLockedMean: null,
  currentFan: null,
  lastLockedFan: null,
};

// S&OP review threshold: any forecast change of ≥10% (either direction) is flagged
// "Action req'd"; everything else is stable.
const ACTION_THRESHOLD = 0.1;

// Feeds the existing "Forecast Change Signals" table:
//   Δ / Δ%                  — batched eval queries (Mean Signed [Percent] Error)
//   Current / Last Locked   — batched BFG forecast means (T / T-1)
// Remaining columns stay placeholder until backed by data.

const fmtDelta = (v: number | null) => {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${Math.round(v).toLocaleString()}`;
};
const fmtPct = (v: number | null) => {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
};
const fmtMean = (v: number | null) => (v == null ? '—' : Math.round(v).toLocaleString());

function toRow(pair: { product: string; customer: string }, change: HomeForecastChange): ForecastChangeSignalRow {
  const { currentMean, lastLockedMean } = change;

  // Δ Units = current forecast − last locked forecast.
  const deltaUnits = currentMean != null && lastLockedMean != null ? currentMean - lastLockedMean : null;
  // Δ % Change = current / last locked − 1 (needs a non-zero last-locked base).
  const deltaPct =
    currentMean != null && lastLockedMean != null && lastLockedMean !== 0 ? currentMean / lastLockedMean - 1 : null;

  const dir: ForecastChangeSignalRow['deltaDirection'] =
    deltaUnits == null || deltaUnits === 0 ? 'neutral' : deltaUnits > 0 ? 'up' : 'down';

  // Flag against the S&OP threshold on the magnitude of the % change.
  const status: ForecastChangeSignalRow['status'] =
    deltaPct != null && Math.abs(deltaPct) >= ACTION_THRESHOLD ? 'action_required' : 'stable';

  return {
    id: `${pair.product}-${pair.customer}`,
    customer: pair.customer,
    sku: pair.product,
    description: '',
    region: 'North America',
    delta: fmtDelta(deltaUnits),
    deltaDirection: dir,
    deltaPercent: fmtPct(deltaPct),
    lastLockedP50: fmtMean(lastLockedMean),
    p50: fmtMean(currentMean),
    lastLockedFan: change.lastLockedFan,
    currentFan: change.currentFan,
    p10: 0,
    p90: 0,
    p50Raw: currentMean ?? 0,
    status,
  };
}

export function ForecastChangeSignalsContainer() {
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (row: ForecastChangeSignalRow) => {
      const params = new URLSearchParams({
        product_id: row.sku,
        ship_to: row.customer,
      });
      const leadTime = String(BFG_DEFAULT_LEADTIME_SPAN[0]);
      for (const fct of DEFAULT_FCTS) params.append('fct', fct);
      params.set('lead_time', leadTime);
      params.set('span', String(BFG_DEFAULT_LEADTIME_SPAN[1]));

      const targetWeeks = availableTargetWeeks(DEFAULT_FCTS, leadTime);
      if (targetWeeks.length) {
        params.set('forecast_start', targetWeeks[0]);
        params.set('forecast_end', targetWeeks[targetWeeks.length - 1]);
      }
      navigate(`${RouterConfig[CanvasRoutes.ForecastExplorer].path}?${params.toString()}`);
    },
    [navigate],
  );

  // All columns come from batched queries (eval Δ/Δ% + batched BFG means),
  // split client-side by pair.
  const { byPair, isLoading } = useHomeForecastChanges();

  const rows = HOME_DFU_PAIRS.map((pair) => toRow(pair, byPair[pairKey(pair.product, pair.customer)] ?? EMPTY_CHANGE));

  return <ForecastChangeSignalsTable rows={rows} isNavigating={isLoading} onRowClick={handleRowClick} pageSize={10} />;
}
